import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getProjectMembers,
    createProjectMember,
    deleteProjectMember,
    ProjectMember
} from '../../Services/UserProjectService';
import { getUserProject } from '../../Services/UserProjectService';

interface TempMember {
    name: string;
    age: string;
    type: 'HUMAN' | 'PET';
    petType: string;
}

const Members: React.FC = () => {
    const navigate = useNavigate();
    const [members, setMembers] = useState<ProjectMember[]>([]);
    const [projectId, setProjectId] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Slides state
    const [currentSlide, setCurrentSlide] = useState(0); // 0: intro text, 1: humans, 2: pets, 3: summary
    const [humanMembers, setHumanMembers] = useState<TempMember[]>([{ name: '', age: '', type: 'HUMAN', petType: '' }]);
    const [petMembers, setPetMembers] = useState<TempMember[]>([{ name: '', petType: '', type: 'PET', age: '' }]);
    const [formError, setFormError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Load project and members
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Get user's project
                const project = await getUserProject();
                if (!project || !project.projectId) {
                    setError('No se encontró el proyecto del usuario');
                    return;
                }

                setProjectId(project.projectId);

                // Get project members
                const projectMembers = await getProjectMembers(project.projectId);
                setMembers(projectMembers);

            } catch (err: any) {
                console.error('Error loading data:', err);
                setError(err.response?.data?.error || 'Error al cargar los datos');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    // Add new member row
    const handleAddRow = () => {
        if (currentSlide === 1) {
            setHumanMembers([...humanMembers, { name: '', age: '', type: 'HUMAN', petType: '' }]);
        } else if (currentSlide === 2) {
            setPetMembers([...petMembers, { name: '', petType: '', type: 'PET', age: '' }]);
        }
    };

    // Update member data
    const handleMemberChange = (index: number, field: 'name' | 'age' | 'petType', value: string) => {
        if (currentSlide === 1) {
            const updated = [...humanMembers];
            updated[index][field] = value;
            setHumanMembers(updated);
        } else if (currentSlide === 2) {
            const updated = [...petMembers];
            updated[index][field] = value;
            setPetMembers(updated);
        }
    };

    // Remove member row
    const handleRemoveRow = (index: number) => {
        if (currentSlide === 1) {
            const updated = humanMembers.filter((_, i) => i !== index);
            setHumanMembers(updated.length > 0 ? updated : [{ name: '', age: '', type: 'HUMAN', petType: '' }]);
        } else if (currentSlide === 2) {
            const updated = petMembers.filter((_, i) => i !== index);
            setPetMembers(updated.length > 0 ? updated : [{ name: '', petType: '', type: 'PET', age: '' }]);
        }
    };

    // Check if can continue
    const canContinue = () => {
        if (currentSlide === 0) {
            return true; // Intro text, always can continue
        } else if (currentSlide === 1) {
            return humanMembers.some(m => m.name.trim() !== '' && m.age.trim() !== '');
        } else if (currentSlide === 2) {
            return true; // Pets are optional
        }
        return false;
    };

    // Navigate slides
    const handleNext = () => {
        if (currentSlide < 3) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const handlePrevious = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    // Submit all members
    const handleSubmitAll = async () => {
        try {
            setSubmitting(true);
            setFormError(null);

            const allMembers = [...humanMembers, ...petMembers].filter(m => m.name.trim() !== '');

            // Validate pets have petType
            const invalidPets = petMembers.filter(m => m.name.trim() !== '' && m.petType.trim() === '');
            if (invalidPets.length > 0) {
                setFormError('Por favor, indica el tipo de mascota para todas las mascotas');
                setSubmitting(false);
                return;
            }

            for (const member of allMembers) {
                const memberData: {
                    name: string;
                    member_type: 'HUMAN' | 'PET';
                    age?: number;
                    pet_type?: string;
                } = {
                    name: member.name.trim(),
                    member_type: member.type
                };

                // Add age for humans
                if (member.type === 'HUMAN' && member.age.trim()) {
                    const age = parseInt(member.age);
                    if (!isNaN(age) && age >= 0 && age <= 200) {
                        memberData.age = age;
                    }
                }

                // Add pet_type for pets (required)
                if (member.type === 'PET' && member.petType.trim()) {
                    memberData.pet_type = member.petType.trim();
                }

                await createProjectMember(projectId, memberData);
            }

            // Reload members and navigate back
            const updatedMembers = await getProjectMembers(projectId);
            setMembers(updatedMembers);
            navigate('/user/dashboard');

        } catch (err: any) {
            console.error('Error creating members:', err);
            setFormError(err.response?.data?.error || 'Error al crear los miembros');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="container-custom">
                <div className="app-main">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const currentMembers = currentSlide === 1 ? humanMembers : petMembers;
    const slideTitle = currentSlide === 1
        ? '¿Quiénes seréis los habitantes habituales de la casa?'
        : currentSlide === 2
            ? '¿Tenéis mascotas?'
            : currentSlide === 3
                ? 'Resumen de miembros'
                : '';

    return (
        <div className="d-flex align-items-center" style={{ width: '100%', minHeight: '100vh' }}>
            {/* Previous Button - 10% width */}
            <div style={{ flex: '0 0 10%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {currentSlide > 0 && (
                    <button
                        onClick={handlePrevious}
                        style={{
                            fontSize: '3rem',
                            width: '80px',
                            height: '80px',
                            border: 'none',
                            background: 'none',
                            color: 'var(--color-dark)',
                            cursor: 'pointer'
                        }}
                    >
                        <i className="bi bi-chevron-left"></i>
                    </button>
                )}
            </div>

            {/* Content Area - 80% width */}
            <div style={{ flex: '0 0 80%', display: 'flex', justifyContent: 'center', padding: '0 2rem' }}>
                <div style={{ maxWidth: '1200px', width: '100%' }}>
                    {/* Error Alert */}
                    {formError && (
                        <div className="alert alert-danger text-center" role="alert" style={{ margin: '0 auto 2rem' }}>
                            <i className="bi bi-exclamation-circle me-2"></i>
                            {formError}
                        </div>
                    )}

                    {/* Intro Text Slide */}
                    {currentSlide === 0 && (
                        <div style={{
                            padding: '0',
                            margin: '0 auto',
                            width: '100%',
                            textAlign: 'center'
                        }}>
                            <p style={{ fontSize: '1.1rem', lineHeight: '2', fontWeight: '300' }}>
                                <strong>¿Cómo funciona?</strong>
                            </p>
                            <p style={{ fontSize: '1.1rem', lineHeight: '2', fontWeight: '300' }}>
                                Mediante esta plataforma, os haremos unos envíos que podréis ir respondiendo a vuestro ritmo y tranquilamente desde vuestra casa.
                            </p>
                            <p style={{ fontSize: '1.1rem', lineHeight: '2', fontWeight: '300' }}>
                                Cuando tengáis un envío nuevo, os avisaremos con un correo y podréis entrar en la plataforma donde lo veréis marcado como “PENDIENTE”.
                            </p>
                            <p style={{ fontSize: '1.1rem', lineHeight: '2', fontWeight: '300' }}>
                                Tendréis que entrar e ir leyendo y respondiendo. Podréis pasar adelante y atrás las veces que queráis, y solamente serán las respuestas definitivas cuando cliquéis al final de todo a “ENVIAR”.
                            </p>
                            <p style={{ fontSize: '1.1rem', lineHeight: '2', fontWeight: '300' }}>
                                Entonces, recibiremos todas vuestras respuestas, que analizaremos y procesaremos con mucho rigor para poderlas convertir en una casa que realmente sea para vosotros.
                            </p>
                            <p style={{ fontSize: '1.1rem', lineHeight: '2', fontWeight: '300' }}>
                                Sin más demora…
                            </p>
                            <br />
                            <br />
                            <p style={{ fontSize: '1.1rem', lineHeight: '2', fontWeight: '300' }}>
                                <strong>Empecemos por saber quién hay al otro lado y quién habitará en la futura casa</strong>
                            </p>                           
                        </div>
                    )}

                    {/* Form Slides (Humans and Pets) */}
                    {(currentSlide === 1 || currentSlide === 2) && (
                        <div style={{
                            padding: '0',
                            maxWidth: '800px',
                            margin: '0 auto',
                            width: '100%'
                        }}>
                            {/* Title */}
                            <p className="text-center mb-5" style={{ fontSize: '1.1rem' }}>
                                {slideTitle}
                            </p>

                            {/* Member Rows */}
                            {currentMembers.map((member, index) => (
                                <div key={index} className="d-flex gap-3 mb-3" style={{ alignItems: 'center' }}>
                                    <div style={{ flex: '1' }}>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={member.name}
                                            onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                                            maxLength={255}
                                            placeholder="Nombre*"
                                            style={{
                                                backgroundColor: '#f5f5f5',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '0.75rem'
                                            }}
                                        />
                                    </div>

                                    <div style={{ flex: '1' }}>
                                        {currentSlide === 1 ? (
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={member.age}
                                                onChange={(e) => handleMemberChange(index, 'age', e.target.value)}
                                                min="0"
                                                max="200"
                                                placeholder="Edad"
                                                style={{
                                                    backgroundColor: '#f5f5f5',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '0.75rem'
                                                }}
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={member.petType}
                                                onChange={(e) => handleMemberChange(index, 'petType', e.target.value)}
                                                maxLength={255}
                                                placeholder="Ej: perro, gato, hamster..."
                                                style={{
                                                    backgroundColor: '#f5f5f5',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '0.75rem'
                                                }}
                                            />
                                        )}
                                    </div>

                                    {currentMembers.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn btn-sm"
                                            onClick={() => handleRemoveRow(index)}
                                            style={{
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                color: '#999',
                                                padding: '0.5rem'
                                            }}
                                        >
                                            <i className="bi bi-x-lg"></i>
                                        </button>
                                    )}
                                </div>
                            ))}

                            {/* Add Button */}
                            <div className="text-center mt-4">
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={handleAddRow}
                                    style={{
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        fontSize: '2rem',
                                        color: '#666',
                                        cursor: 'pointer',
                                        padding: '0',
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Summary Slide */}
                    {currentSlide === 3 && (
                        <div style={{
                            padding: '0',
                            maxWidth: '800px',
                            margin: '0 auto',
                            width: '100%'
                        }}>
                            <p className="text-center mb-5" style={{ fontSize: '1.1rem' }}>
                                Por favor, revisa que esté todo correcto
                            </p>

                            {/* All Members List */}
                            <div className="d-flex flex-column gap-2 mb-4">
                                {[...humanMembers, ...petMembers].filter(m => m.name.trim() !== '').map((member, index) => (
                                    <div key={index} className="p-3" style={{ backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                        <strong>{member.name}</strong>
                                        {member.type === 'HUMAN' && member.age && ` - ${member.age} años`}
                                        {member.type === 'PET' && member.petType && ` - ${member.petType}`}
                                    </div>
                                ))}
                            </div>

                            {/* Submit Button */}
                            <div className="text-center mt-5">
                                <button
                                    type="button"
                                    className="btn btn-dark"
                                    onClick={handleSubmitAll}
                                    disabled={submitting}
                                    style={{
                                        borderRadius: '0',
                                        padding: '0.5rem 1.5rem'
                                    }}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            GUARDANDO...
                                        </>
                                    ) : (
                                        'GUARDAR'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Next Button - 10% width */}
            <div style={{ flex: '0 0 10%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {currentSlide < 3 && (
                    <button
                        onClick={handleNext}
                        disabled={!canContinue()}
                        style={{
                            fontSize: '3rem',
                            width: '80px',
                            height: '80px',
                            border: 'none',
                            background: 'none',
                            color: canContinue() ? 'var(--color-dark)' : '#ccc',
                            cursor: canContinue() ? 'pointer' : 'not-allowed'
                        }}
                    >
                        <i className="bi bi-chevron-right"></i>
                    </button>
                )}
            </div>
        </div>
    );
};

export default Members;
